<template>
    <div class="cart-order-form row justify-content-center">
        <div class="col-12">
            <loading-included v-if="loading" class="text-center mb-5 font-weight-bold" />

            <template v-if="loaded">
                <div class="cart-form">
                    <div class="catalog">
                        <div v-if="alerts && alerts.cart">
                            <alert :alert="alert" :key="alert.id" v-for="alert in alerts.cart" />
                        </div>
                        <div v-if="productsByCategories && productsByCategories.length === 0" class="text-center font-weight-bold h4 mt-4">
                            Aucun produit n'est disponible pour cette date de livraison
                        </div>
                        <div class="catalog-grid">
                            <template v-for="(categoryWithProducts, categoryIndex) in productsByCategories">
                                <button
                                    type="button"
                                    class="category"
                                    :class="'category-' + categoryWithProducts.id + '-btn'"
                                    :key="'category-' + categoryWithProducts.id + '-btn'"
                                    @click="toggleDisplayCategory(categoryIndex)"
                                >
                                    <span class="category-images">
                                        <img v-for="product in first6(categoryWithProducts.products)"
                                             :src="product.default_image_preview_url"/>
                                    </span>
                                    <span class="category-name">
                                        {{ categoryWithProducts.name }}
                                    </span>
                                    <button v-if="countOrderedInCategory(categoryWithProducts)" type="button" class="ordered-count">
                                        <i class="fad fa-shopping-cart"></i> {{ countOrderedInCategory(categoryWithProducts) }}
                                    </button>
                                </button>
                                <div
                                    v-show="categoryDisplayed == categoryIndex"
                                    class="category-products-container"
                                    :class="'category-' + categoryWithProducts.id + '-products'"
                                    :key="'category-' + categoryWithProducts.id + '-products'"
                                >
                                    <div class="category-products-title-bar">
                                        <h2 :id="'categorie-' + categoryWithProducts.id">{{ categoryWithProducts.name }}</h2>
                                        <button type="button" class="btn btn-outline-dark" @click="categoryDisplayed = null">
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </div>
                                    <div class="category-products">
                                        <div v-for="product in _.orderBy(categoryWithProducts.products, ['sku'])"
                                             class="product"
                                             :class="['product-' + product.sku, productSwitch(product, 'product-not-allowed', 'product-out-of-stock', 'product-soon')]"
                                             :key="product.sku">
                                            <button
                                                type="button"
                                                class="product-btn"
                                                @click="incrementQuantity(product)"
                                            >
                                                <span class="product-image">
                                                    <img :src="product.default_image_preview_url" class="product-image" :title="product.name" height="120" width="150">
                                                    <span class="product-banner">
                                                        {{ productSwitch(product, 'Produit non autorisé', 'Rupture de stock', 'Bientôt disponible') }}
                                                    </span>
                                                </span>
                                                <div class="product-name-and-price">
                                                    <span class="product-name">
                                                        {{ product.short_name }}
                                                    </span>
                                                    <span v-if="customer.type === 'customer_group' && product.price" class="product-price">
                                                        {{ formatPrice(product.price.amountTaxesInc) }} €
                                                    </span>
                                                </div>
                                            </button>
                                            <button
                                                v-if="quantities[product.sku]"
                                                type="button"
                                                class="ordered-count"
                                                @click="showProductModal(product)"
                                            >
                                                <i class="fad fa-shopping-cart"></i> {{ quantities[product.sku] }}
                                            </button>
                                            <button
                                                type="button"
                                                class="product-info"
                                                @click="showProductModal(product)"
                                            >
                                                <i class="far fa-info-circle"></i>
                                            </button>
                                            <button
                                                v-if="product.product_brand && product.product_brand.id"
                                                type="button"
                                                class="product-brand"
                                                @click="showProductModal(product)"
                                            >
                                                <img :src="product.product_brand.logo_dark_url" :alt="product.product_brand.name" :title="product.product_brand.name">
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div class="cart">
                        <div class="cart-client">
                            <div>
                                {{ customerName }}
                            </div>
                            <div v-if="customerName !== customer.name" class="small">
                                {{ customer.name }}
                            </div>
                            <div>
                                Livraison {{ formattedDeliveryDate }}
                            </div>

                            <div v-if="(customer.type == 'pickup_point' && !subcustomer) || customer.type == 'store'" class="mb-2">
                                <ReOrderingModal
                                    :customer="customer"
                                    :products="products"
                                    :hide-not-authorized-products-warnings="!blockNotOrderable"
                                    :previous-orders-action="previousOrdersAction"
                                    @submit="onReOrderingModalSubmit"
                                />
                            </div>
                        </div>
                        <div class="cart-title-bar d-flex flex-row ">
                            <div class="flex-fill">
                                <div class="h3 mb-0"><i class="fad fa-shopping-cart"></i> Panier</div>
                                <div class="small" :class="[customer.type === 'customer_group' ? 'ml-2' : 'ml-4']">
                                    <template v-if="customer.type === 'customer_group'">
                                        Total {{ formatPrice(totalAmountCartProducts) }} € -
                                    </template>
                                    {{ totalCartProducts }} produit(s)
                                </div>
                            </div>
                            <div class="text-right py-1">
                                <button
                                    v-if="loaded"
                                    type="button"
                                    class="btn btn-success btn-sm py-2"
                                    :disabled="!canClickNext"
                                    @click="onClickNext"
                                >
                                    Suivant
                                    <i class="fad fa-shopping-cart"></i>
                                    <i class="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                        <div class="cart-content" ref="cart-content">
                            <div
                                v-for="orderedProduct in cartProducts"
                                :key="'cart-product-' + orderedProduct.sku"
                                :ref="'cart-product-' + orderedProduct.sku.toLowerCase()"
                                class="cart-product"
                                :class="['cart-product-' + orderedProduct.sku, productSwitch(products[orderedProduct.sku], 'cart-product-not-allowed', 'cart-product-out-of-stock', 'cart-product-soon')]"
                            >
                                <a  class="cart-product-description"
                                    :href="'#categorie-' + orderedProduct.fk_category"
                                    @click="onClickCartProduct(orderedProduct)"
                                >
                                <span class="cart-product-banner">
                                    {{ productSwitch(products[orderedProduct.sku], 'Produit non autorisé', 'Rupture de stock', 'Bientôt disponible') }}
                                </span>
                                    <div class="cart-product-image">
                                        <img :src="orderedProduct.default_image_preview_url" :title="orderedProduct.name">
                                    </div>
                                    <div class="cart-product-name">
                                        {{ orderedProduct.short_name }}
                                    </div>
                                </a>
                                <div v-if="orderedProduct.nb_offered" class="alert alert-info product-quantity-offered my-1 py-1 font-weight-bold">
                                    <i class="far fa-gift"></i> {{ orderedProduct.nb_offered }} {{ pluralize('offert', orderedProduct.nb_offered) }}
                                </div>
                                <div class="cart-product-actions">
                                    <div class="cart-product-actions-form">
                                        <button
                                            type="button"
                                            class="btn btn-sub"
                                            :disabled="quantities[orderedProduct.sku] <= 0"
                                            @click="decrementQuantity(orderedProduct, false)"
                                        >
                                            <i v-if="quantities[orderedProduct.sku] > 1" class="fas fa-minus"></i>
                                            <i v-else class="fas fa-trash"></i>
                                        </button>
                                        <input-integer-positive
                                            class="form-control product-quantity"
                                            size="4"
                                            :allow-zero="true"
                                            v-model="quantities[orderedProduct.sku]"
                                            :disabled="!isOrderable(orderedProduct)"
                                        />
                                        <button
                                            type="button"
                                            class="btn btn-add"
                                            :disabled="!isOrderable(orderedProduct)"
                                            @click="incrementQuantity(orderedProduct, false)"
                                        >
                                            <i class="fas fa-plus"></i>
                                        </button>
                                    </div>
                                    <div class="cart-product-total" v-if="customer.type === 'customer_group'">
                                        {{ formatPrice(quantities[orderedProduct.sku] * parseFloat(orderedProduct.price.amountTaxesInc).toFixed(2)) }} €
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <ErrorsZone :errors="errors"/>

            <template>
                <modal ref="productModal" size="large" class="product-information-modal" @closed="onCloseProductModal">
                    <template #body="sp">
                        <template v-if="displayedProduct">
                            <div class="product-information-modal-title h4 mb-3">{{ displayedProduct.name }}</div>

                            <div class="row">
                                <div class="col-12 col-sm-4 py-4"
                                     :class="productSwitch(displayedProduct, 'modal-product-not-allowed', 'modal-product-out-of-stock', 'modal-product-soon')">
                                    <div class="product-information-modal-image">
                                        <img class="w-100" :src="displayedProduct.default_image_preview_url" :title="displayedProduct.name">
                                    </div>
                                    <div class="text-center modal-product-banner">
                                        {{ productSwitch(displayedProduct, 'Produit non autorisé', 'Rupture de stock', 'Bientôt disponible') }}
                                        <div class="small"
                                             v-if="productSwitch(displayedProduct, false, false, true)">
                                            Disponible à partir du<br />
                                            {{ productStockDate(displayedProduct) }}
                                        </div>
                                    </div>
                                    <div class="text-center mt-2 mb-3" v-if="displayedProduct.price && displayedProduct.computed_prices">
                                        <div class="product-price" title="Tarif">
                                            Tarif :
                                            <template v-if="!displayedProduct.price.hasTaxesInc">
                                                {{ formatPrice(displayedProduct.price.amountTaxesFree) }} € HT
                                            </template>
                                            <template v-else>
                                                {{ formatPrice(displayedProduct.price.amountTaxesInc) }} € TTC
                                            </template>
                                            <span v-if="displayedProduct.unit === 'kg' && customer.type !== 'customer_group'">
                                                / {{ displayedProduct.unit }}
                                            </span>
                                        </div>
                                        <div class="small product-vat">
                                            TVA : {{ parseFloat(displayedProduct.price.taxrate).toFixed(1).replace('.', ',') }} %
                                        </div>
                                        <div class="small product-price text-muted mt-2" title="Prix de vente conseillé"  v-if="customer.type !== 'customer_group' && displayedProduct.computed_prices && displayedProduct.computed_prices.advised">
                                            <abbr>Prix conseillé</abbr> :
                                            <template v-if="!displayedProduct.computed_prices.advised.hasTaxesInc">
                                                {{ formatPrice(displayedProduct.computed_prices.advised.amountTaxesFree) }} € HT
                                            </template>
                                            <template v-else>
                                                {{ formatPrice(displayedProduct.computed_prices.advised.amountTaxesInc) }} € TTC
                                            </template>
                                            <span v-if="displayedProduct.unit === 'kg'">
                                                / {{ displayedProduct.unit }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="displayedProduct.nb_offered" class="alert alert-info product-quantity-offered my-1 py-1 font-weight-bold">
                                        <i class="far fa-gift"></i> {{ displayedProduct.nb_offered }} {{ pluralize('offert', displayedProduct.nb_offered) }}
                                    </div>
                                    <div class="cart-product-actions">
                                        <div class="cart-product-actions-form">
                                            <button
                                                type="button"
                                                class="btn btn-sub"
                                                :disabled="quantities[displayedProduct.sku] <= 0"
                                                @click="decrementQuantity(displayedProduct)"
                                            >
                                                <i v-if="quantities[displayedProduct.sku] > 1" class="fas fa-minus"></i>
                                                <i v-else class="fas fa-trash"></i>
                                            </button>
                                            <input-integer-positive
                                                class="form-control product-quantity"
                                                size="4"
                                                :allow-zero="true"
                                                v-model="quantities[displayedProduct.sku]"
                                                :disabled="!isOrderable(displayedProduct)"
                                            />
                                            <button
                                                type="button"
                                                class="btn btn-add"
                                                @click="incrementQuantity(displayedProduct)"
                                                :disabled="!isOrderable(displayedProduct)"
                                            >
                                                <i class="fas fa-plus"></i>
                                            </button>
                                        </div>
                                        <div class="cart-product-total" v-if="customer.type === 'customer_group'">
                                            {{ formatPrice(quantities[displayedProduct.sku] * parseFloat(displayedProduct.price.amountTaxesInc).toFixed(2)) }} €
                                        </div>
                                    </div>
                                    <div class="product-information-modal-brand mt-4 px-4" v-if="displayedProduct.product_brand && displayedProduct.product_brand.id">
                                        <img :src="displayedProduct.product_brand.logo_dark_url" :title="displayedProduct.product_brand.name" :alt="displayedProduct.product_brand.name">
                                    </div>
                                </div>
                                <div class="col-12 col-sm-8">
                                    <div class="product-information-modal-description text-justify my-3"
                                         v-html="displayedProduct.notes"></div>

                                    <div class="product-information-modal-details border border-dark my-3 py-2 px-3">
                                        <div v-if="displayedProduct.ingredients" class="product-information-modal-details-section my-1">
                                            <span class="product-information-modal-details-section-title">
                                                Ingrédients :
                                                <small>
                                                    <i class="text-muted">
                                                        * Ingrédients issus de l'agriculture biologique
                                                    </i>
                                                </small>
                                            </span>
                                            <br>
                                            {{ displayedProduct.ingredients }}
                                        </div>

                                        <div class="product-information-modal-details-section my-1">
                                            <span class="product-information-modal-details-section-title">
                                                Allergènes :
                                            </span>
                                            <template v-if="displayedProduct.allergens">
                                                {{ displayedProduct.allergens }}
                                            </template>
                                            <span v-else class="text-muted">Sans</span>
                                        </div>

                                        <div v-if="displayedProduct.storage_days" class="product-information-modal-details-section my-1">
                                            <span class="product-information-modal-details-section-title">
                                                Conservation :
                                            </span>
                                            {{ displayedProduct.storage_days }} jours
                                        </div>

                                        <div class="product-information-modal-details-section my-1"
                                             v-if="displayedProduct.display_weight">
                                            <span class="product-information-modal-details-section-title">Poids :</span>
                                            {{ displayedProduct.display_weight }}g environ
                                            <div v-if="displayedProduct.calculation_weight && displayedProduct.calculation_weight !== displayedProduct.display_weight">
                                                <small>
                                                    <i class="text-muted">
                                                        Les poids sont susceptibles de varier en fonction de la fabrication.
                                                    </i>
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>
                    <template #footer>
                        <button type="button" class="btn btn-secondary btn-close" @click="closeProductModal()">
                            Fermer
                        </button>
                    </template>
                </modal>
            </template>

            <hr>

            <button type="button" class="btn btn-outline-dark float-left" @click="onClickBack">
                <i class="fas fa-chevron-left"></i> Retour
            </button>

            <button type="button" class="btn btn-success btn-next float-right" @click="onClickNext"
                    :disabled="!canClickNext" v-if="loaded">
                Suivant <i class="fas fa-chevron-right"></i>
            </button>
        </div>
    </div>
</template>

<script>
    import {v4 as uuidv4} from 'uuid';
    import {useGetLoader} from "./compositions/axios/loadingRequest";
    import {momentDay, momentDayFormatYmd} from "../moment-with-helpers";
    import {formatPrice, pluralize, storageAvailable} from '../helpers';

    import ErrorsZone from "./ErrorsZone";
    import LoadingIncluded from "./LoadingIncluded"
    import InputIntegerPositive from "./InputIntegerPositive";
    import Modal from "./Modal";
    import ReOrderingModal from "./ReOrderingModal"

    export default {
        components: {
            ErrorsZone,
            'loading-included': LoadingIncluded,
            'input-integer-positive': InputIntegerPositive,
            'modal': Modal,
            ReOrderingModal,
        },

        props: {
            order: {
                type: Object,
                required: false,
            },
            customer: {
                type: Object,
                required: true,
            },
            subcustomer: {
                type: Object,
                required: false,
            },
            orderedProducts: {
                type: Object,
                required: false,
            },
            deliveryDate: {
                type: Object,
                required: false,
            },
            endPreparationDate: {
                type: Object,
                required: false,
            },
            isRecurrent: {
                type: Boolean,
                required: false,
                default: false,
            },
            alertsAction: {
                type: String,
                required: false,
            },
            productsAction: {
                type: String,
                required: false,
            },
            previousOrdersAction: {
                type: String,
                required: false,
            },
            blockNotOrderable: {
                type: Boolean,
                required: false,
            },
            blockNotAuthorized: {
                type: Boolean,
                required: false,
                default: false,
            },
            useLocalStorage: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data: function () {
            return {
                productsByCategories: [],
                categoryDisplayed: null,
                displayedProduct: null,
                alerts: null,

                quantities: {},

                needReload: true,

                comment: null,

                localStorageAvailable: storageAvailable("localStorage"),
            };
        },

        async created() {
            await this.loadAlerts();
        },

        async beforeUpdate() {
            if (this.needReload) {
                this.needReload = false;
                await this.loadProducts();
            }
        },

        async mounted() {
            if (this.customer && this.customer.type === 'customer_group') {
                await this.loadProducts();
                if (this.useLocalStorage && this.localStorageAvailable) {
                    if (!localStorage.getItem('cart_id')) {
                        localStorage.setItem('cart_id', uuidv4());
                    }
                    this.loadProductsQuantitiesFromLocalStorage();
                }
            }
        },

        watch: {
            async customer() {
                this.needReload = true;
            },

            async deliveryDate() {
                this.needReload = true;
            },

            async endPreparationDate() {
                this.needReload = true;
            },

            cartProducts() {
                if (this.useLocalStorage && this.localStorageAvailable) {
                    this.$nextTick(() => {
                        localStorage.setItem('cart', JSON.stringify(this.quantities));
                    });
                }
            },
        },

        methods: {
            loadProductsQuantitiesFromLocalStorage() {
                const quantities = localStorage.getItem('cart');
                if (quantities) {
                    this.quantities = JSON.parse(quantities);
                }
            },

            displayCategory(fk_category) {
                for (const index in this.productsByCategories) {
                    if (this.productsByCategories[index].id === fk_category) {
                        this.categoryDisplayed = index;
                        return;
                    }
                }
            },

            toggleDisplayCategory(index) {
                if (this.categoryDisplayed === index) {
                    this.categoryDisplayed = null;
                } else {
                    this.categoryDisplayed = index;
                }
            },

            isOrderable(product) {
                if (this.blockNotOrderable) {
                    if (product.computed_stock_status === 'out-of-stock') {
                        return false;
                    }
                    if (!product.authorization) {
                        return false;
                    }
                }
                if (this.blockNotAuthorized && !product.authorization) {
                    return false;
                }
                return true;
            },

            incrementQuantity(product, scroll = true) {
                if(!this.isOrderable(product)) {
                    alert("Ce produit n'est pas commandable.");
                    return false;
                }
                this.$set(this.quantities, product.sku, this.quantities[product.sku] + 1);
                if(scroll) {
                    this.scrollToProduct(product);
                }
            },

            scrollToProduct(product) {
                this.$nextTick(function() {
                    let productRef = 'cart-product-'+product.sku.toLowerCase();
                    let productDiv = this.$refs[productRef][0];

                    productDiv.classList.remove('cart-product-blink-once');
                    setTimeout(function() {
                        productDiv.classList.add('cart-product-blink-once');
                    }, 1);
                    if(document.body.clientWidth > 992) { // Scroll only if we are on a double-column layout
                        this.$refs['cart-content'].scrollTo(0, productDiv.offsetTop);
                    }
                });
            },

            decrementQuantity(product, scroll = true) {
                if (this.quantities[product.sku] > 0) {
                    if (this.quantities[product.sku] != 1) {
                        this.$set(this.quantities, product.sku, this.quantities[product.sku] - 1);
                        if(scroll) {
                            this.scrollToProduct(product);
                        }
                    } else if(confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
                        this.$set(this.quantities, product.sku, this.quantities[product.sku] - 1);
                    }
                }
            },

            onReOrderingModalSubmit({orderedProducts, comment}) {
                if (orderedProducts) {
                    for (const sku in this.quantities) {
                        this.$set(this.quantities, sku, 0);
                    }

                    for (const sku in orderedProducts) {
                        if (this.products.hasOwnProperty(sku)) {
                            if (this.products[sku].authorization || !this.blockNotOrderable) {
                                this.$set(this.quantities, sku, orderedProducts[sku]);
                            }
                        }
                    }
                }
                this.comment = null;
                if (comment) {
                    this.comment = comment;
                }
            },

            onClickBack() {
                this.$emit('back');
            },

            onClickNext() {
                this.$emit('submit', {
                    cartProducts: this.cartProducts,
                    comment: this.comment || null,
                });
                this.comment = null;
            },

            async loadProducts() {
                const url = route(this.productsAction, {
                    customerType: this.customer ? this.customer.type : null,
                    customerId: this.customer ? this.customer.id : null,
                    deliveryDateYmd: this.deliveryDate ? momentDayFormatYmd(this.deliveryDate.date) : null,
                    endPreparationDateYmd: this.endPreparationDate ? momentDayFormatYmd(this.endPreparationDate.date) : null,
                });

                this.productsByCategories = await this.load(url);

                for (const index in this.productsByCategories) {
                    if (this.productsByCategories[index].products) {
                        for (const sku in this.productsByCategories[index].products) {
                            let quantity = 0;
                            if (this.orderedProducts && this.orderedProducts[sku] && this.orderedProducts[sku].nb_ordered > 0) {
                                quantity = this.orderedProducts[sku].nb_ordered;
                            }
                            if (this.quantities && this.quantities[sku]) {
                                quantity = this.quantities[sku];
                            }
                            this.$set(this.quantities, sku, quantity);
                        }
                    }
                }
            },

            async loadAlerts() {
                if(this.alertsAction) {
                    this.alerts = await this.load(this.alertsAction);
                }
            },

            countOrderedInCategory(categoryWithProducts) {
                let count = 0;
                for (const sku in this.cartProducts) {
                    if (this.cartProducts[sku].fk_category === categoryWithProducts.id) {
                        count += parseInt(this.quantities[sku]);
                    }
                }
                return count;
            },

            first6(item) {
                if (item) {
                    const keys = Object.keys(item).slice(0, 6);

                    let items = [];
                    for (const key of keys) {
                        items.push(item[key]);
                    }

                    return items;
                }
                return [];
            },

            showProductModal(product) {
                this.displayedProduct = product;
                this.$refs.productModal.showModal();
            },

            closeProductModal() {
                this.onCloseProductModal();
                this.$refs.productModal.closeModal();
            },

            onCloseProductModal() {
                this.displayedProduct = null;
            },

            onClickCartProduct(product) {
                this.displayCategory(product.fk_category);
                this.showProductModal(product);
            },

            productStockDate(product) {
                if(product.stock_date) {
                    return momentDay(product.stock_date).format('dddd Do MMMM YYYY');
                }
                return null;
            },

            productSwitch(product, notAllowed, outofStock, soon, otherwise = '') {
                if(product) {
                    if (!product.authorization) {
                        return notAllowed;
                    } else {
                        if (product.computed_stock_status === 'out-of-stock') {
                            if(product.stock_date) {
                                return soon;
                            } else {
                                return outofStock;
                            }
                        }
                    }
                }
                return otherwise;
            },

            formatPrice,

            pluralize,
        },

        computed: {
            customerName() {
                if (this.subcustomer) {
                    return this.subcustomer.name
                }
                return this.customer.name;
            },

            canClickNext() {
                return Object.keys(this.cartProducts).length > 0;
            },

            products() {
                let products = {};

                for (const index in this.productsByCategories) {
                    if (this.productsByCategories[index].products) {
                        for (const sku in this.productsByCategories[index].products) {
                            products[sku] = this.productsByCategories[index].products[sku];
                        }
                    }
                }

                return products;
            },

            cartProducts() {
                let products = {};

                for (const sku in this.products) {
                    if (this.quantities[sku] > 0) {
                        let product = this.products[sku];
                        product.nb_ordered = this.quantities[sku];
                        if (this.order && this.order.ordered_products_by_sku) {
                            product.nb_offered = _.get(this.order.ordered_products_by_sku, [sku , 'pivot', 'quantity_offered'], 0);
                        } else {
                            product.nb_offered = 0;
                        }

                        products[sku] = product;
                    }
                }

                return products;
            },

            totalCartProducts() {
                let totalProducts = 0;
                if (this.cartProducts) {
                    for (const sku in this.cartProducts) {
                        if (this.quantities[sku] > 0) {
                            totalProducts += this.quantities[sku];
                        }
                    }
                }

                return totalProducts;
            },

            totalAmountCartProducts() {
                let total = 0.0;
                if (this.cartProducts) {
                    for (const sku in this.cartProducts) {
                        if (this.quantities[sku] > 0) {
                            total += this.quantities[sku] * parseFloat(this.cartProducts[sku].price.amountTaxesInc).toFixed(2);
                        }
                    }
                }
                return total;
            },

            formattedDeliveryDate() {
                if (this.deliveryDate) {
                    return momentDay(this.deliveryDate.date).format('dddd Do MMMM YYYY');
                } else if (this.order) {
                    return momentDay(this.order.delivery_date).format('dddd Do MMMM YYYY');
                }
            },
        },
    }
</script>

<style lang="scss" scoped>

    $notAllowedColor: #7f3737;
    $notAllowedColorLite: #deb3b3;
    $notAllowedColorLite2: #efd3d3;
    $outOfStockColor: #7f7337;
    $outOfStockColorLite: #ded8b3;
    $outOfStockColorLite2: #efead3;
    $soonColor: #37497f;
    $soonColorLite: #b3bcde;
    $soonColorLite2: #d3daef;
    $blinkColorLite: #decdb3;
    $blinkColorLite2: #efe5d3;

    .title-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .cart-form {
        width: 100%;
        bottom: 0;
        display: flex;
        height: calc(100vh - 190px);
    }

    .catalog {
        padding-left: 5px;
        padding-right: 5px;
        flex-grow: 1;
        overflow-y: auto;
        height: 100%;
        box-shadow: 0 0 15px -10px rgba(0, 0, 0, 0.5) inset;
    }

    .catalog-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(162px, 1fr));
        grid-auto-flow: row dense;
        grid-auto-rows: max-content;
    }

    .category {
        position: relative;
        border: none;
        background: #ffffff;
        font: inherit;
        width: 150px;
        display: inline-flex;
        margin: 9px;
        padding: 5px;
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
        flex-direction: column;
        justify-content: space-between;
        border-radius: 3px;
    }

    .product {
        position: relative;
        border: none;
        background: #ffffff;
        font: inherit;
        width: 150px;
        display: inline-flex;
        margin: 9px;
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
        flex-direction: column;
        justify-content: space-between;
        border-radius: 3px;
        padding: 0;
        z-index: 1;
    }

    .product-btn {
        border: none;
        display: inline-flex;
        flex-direction: column;
        padding: 0;
        height: 100%;
    }

    .product:hover, .category:hover {
        cursor: pointer;
        box-shadow: 0 0 7px 0 rgba(0, 0, 0, 0.5);
        transform: scale(1.02);
    }

    .product:active, .category:active {
        transform: scale(1.01);
    }

    .category-images {
        text-align: left;
    }

    .category-images img {
        height: 40px;
        width: 40px;
        object-fit: cover;
        object-position: center;
        display: inline-block;
        margin: 3px;
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
    }

    .product-image {
        position: relative;
        width: 100%;
    }

    .product-image img {
        width: 100%;
        max-height: 120px;
        object-fit: cover;
        object-position: center;
    }

    .product-information-modal-image img {
        max-height: 300px;
        object-fit: cover;
        object-position: center;
    }

    .product-name-and-price {
        min-height: 50px;
        height: 100%;
        width: 100%;
        padding: 9px 6px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .category-name {
        width: 100%;
        min-height: 50px;
        text-align: center;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 9px 6px;
    }

    .product-name {
        flex-grow: 1;
        font-weight: bold;
        position: relative;
    }

    .product-btn .product-price {
        width: 100%;
        text-align: center;
    }

    .product-not-allowed .product-info,
    .product-not-allowed .product-banner,
    .cart-product.cart-product-not-allowed .cart-product-banner,
    .modal-product-not-allowed .modal-product-banner {
        background-color: $notAllowedColor;
    }

    .product-not-allowed .product-name-and-price,
    .cart-product.cart-product-not-allowed:nth-child(2n+1) {
        background: $notAllowedColorLite;
    }

    .modal-product-not-allowed,
    .cart-product.cart-product-not-allowed {
        background: $notAllowedColorLite2;
    }

    .product-out-of-stock .product-info,
    .product-out-of-stock .product-banner,
    .cart-product.cart-product-out-of-stock .cart-product-banner,
    .modal-product-out-of-stock .modal-product-banner {
        background-color: $outOfStockColor;
    }

    .product-out-of-stock .product-name,
    .cart-product.cart-product-out-of-stock:nth-child(2n+1) {
        background: $outOfStockColorLite;
    }

    .modal-product-out-of-stock,
    .cart-product.cart-product-out-of-stock {
        background: $outOfStockColorLite2;
    }

    .product-soon .product-info,
    .product-soon .product-banner,
    .cart-product.cart-product-soon .cart-product-banner,
    .modal-product-soon .modal-product-banner {
        background-color: $soonColor;
    }

    .product-soon .product-name,
    .cart-product.cart-product-soon:nth-child(2n+1) {
        background: $soonColorLite;
    }

    .modal-product-soon,
    .cart-product.cart-product-soon {
        background: $soonColorLite2;
    }

    .cart-product.cart-product-blink-once {
        animation: blink-once 0.6s ease-in-out;
    }
    .cart-product.cart-product-soon:nth-child(2n+1) {
        animation: blink-once2 0.6s ease-in-out;
    }

    @keyframes blink-once {
        0% {
            background: $blinkColorLite;
        }
        100% {
            background: $blinkColorLite;
        }
    }

    @keyframes blink-once2 {
        0% {
            background: $blinkColorLite2;
        }
        100% {
            background: $blinkColorLite2;
        }
    }

    .product-info {
        position: absolute;
        top: 0;
        left: 0;
        padding: 0;
        width: 50px;
        height: 50px;
        line-height: 50px;
        border: 0;
        border-bottom-right-radius: 50%;
        background-color: #2993ff;
        z-index: 99;
        color: #ffffff;
        font-weight: bold;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    }

    .product-brand {
        position: absolute;
        top: 70px;
        right: 0;
        padding: 0;
        width: 50px;
        height: 40px;
        border: 0;
        border-bottom-left-radius: 35%;
        border-top-left-radius: 35%;
        background-color: #ffffff;
        z-index: 99;
    }

    .product-brand img {
        max-width: 40px;
        max-height: 40px;
        vertical-align: middle;
    }

    .modal-product-banner {
        color: #ffffff;
    }

    .category-products-container {
        padding: 20px;
        width: 100%;
        background-color: #e0e0e0;
        grid-column: 1 / -1;
    }

    .category-products-title-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .category-products-title-bar h2 {
        margin: 0;
    }

    .category-products {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        grid-gap: 9px;
    }

    .category-products .product-banner {
        position: absolute;
        bottom: -6px;
        right: 0;
        width: 100%;
        height: 16px;
        line-height: 16px;
        font-size: 80%;
        color: #ffffff;
        font-weight: normal;
        z-index: 110;
    }

    .ordered-count {
        position: absolute;
        top: 0;
        right: 0;
        min-width: 50px;
        height: 50px;
        line-height: 50px;
        border: 0;
        border-bottom-left-radius: 50%;
        background-color: #ffffff;
        z-index: 99;
        color: #212529;
        font-weight: bold;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    }

    .cart {
        display: flex;
        height: 100%;
        padding: 9px;
        width: 300px;
        min-width: 300px;
        flex-grow: 0;
        flex-shrink: 0;
        flex-direction: column;
    }

    .cart-title-bar {
        border-bottom: 1px solid black;
        border-top: 1px solid black;
        margin-top: 5px;
        margin-bottom: 5px;
        padding-top: 5px;
        padding-bottom: 5px;
        min-height: 60px;
    }

    .cart-title-bar h2 {
        margin: 0;
        flex-grow: 1;
    }

    .cart-content {
        position: relative;
        flex-grow: 1;
        overflow-y: auto;
        padding: 0 9px 0 0;
    }

    .cart-actions {
        padding: 9px 0;
        flex-grow: 0;
        border-top: 1px solid black;
    }

    .cart-product {
        padding: 9px;
        width: 100%;
        position: relative;
    }

    .cart-product:nth-child(2n+1) {
        background: rgba(0,0,0,0.1);
    }

    .cart-product-description {
        display: flex;
        margin-bottom: 5px;
        text-decoration: none;
        color: inherit
    }

    .cart-product-image {
        flex-grow: 0;
    }

    .cart-product-image img {
        max-width: 50px;
        max-height: 50px;
    }

    .cart-product-name {
        flex-grow: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        padding: 5px;
    }

    .cart-product-actions {
        display: flex;
        align-items: center;
    }

    .cart-product-actions-title {
        font-weight: bold;
        font-size: 1.3rem;
    }

    .cart-product-actions-form {
        display: flex;
    }

    .cart-product-actions-trash {
        justify-content: flex-end;
    }

    .cart-product-total {
        font-weight: normal;
        text-align: right;
        flex-grow: 1;
    }

    .cart-product .cart-product-banner {
        position: absolute;
        top: 0;
        right: 0;
        min-width: 50%;
        text-align: center;
        height: 16px;
        line-height: 16px;
        font-size: 80%;
        color: #ffffff;
        font-weight: normal;
        z-index: 120;
    }

    .btn-add {
        margin-left: 5px;
        background: #1e7e34;
        color: #ffffff;
    }

    .btn-sub {
        margin-right: 5px;
        background: #cc1a2a;
        color: #ffffff;
    }

    .btn-trash {
        background: #cc1a2a;
        color: #ffffff;
    }

    .product-information-modal-brand {

    }

    .product-information-modal-brand img {
        width: 100%;
    }

    .product-information-modal {
        &-details-section-title {
            font-weight: bold;
        }
    }

    @media (max-width: 992px) {
        .cart-form {
            display: block;
            height: inherit;
        }
        .cart {
            width: inherit;
        }
    }
</style>
